import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../contexts/DataContext'; // Importer useData
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ComplianceBar from '../components/ComplianceBar';

const { FiCheck, FiShield, FiBriefcase, FiDollarSign, FiInfo, FiArrowLeft, FiUsers } = FiIcons;

const PricingInvestigationPage = () => {
  const navigate = useNavigate();
  // Hent dynamiske priser fra context
  const { investigationPricing } = useData();

  // Fallback verdier hvis context ikke er lastet (sikkerhet)
  const prices = investigationPricing || {
    company: { standard: 6900, externalFrom: 15000 },
    investigator: { basic: 990, pro: 2990, partner: 0 },
    commission: { consultant: 20, advocate: 15, partnerMin: 10, partnerMax: 15 }
  };

  const formatPrice = (price) => {
    return price ? price.toLocaleString('no-NO') : '0';
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-slate-900">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <SafeIcon icon={FiShield} className="text-blue-600 text-xl" />
            <span className="font-bold text-lg">HMSVarsling</span>
          </div>
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900 flex items-center gap-1 text-sm font-medium transition-colors">
            <SafeIcon icon={FiArrowLeft} /> Tilbake
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-slate-900 text-white py-20 text-center px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-slate-900 opacity-50"></div>
        <div className="relative z-10">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight">
            Priser for utredning
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
            Oversikt over kostnader for bedrifter og vilkår for våre partnere.
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* SECTION 1: COMPANY PRICING */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 flex items-center justify-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg text-blue-600"><SafeIcon icon={FiBriefcase} /></div>
              Ekstern utredning for bedrifter
            </h2>
            <p className="text-slate-500 mt-3 text-lg">Hva koster det å få bistand til en varslingssak?</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Card 1: Default */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-2xl shadow-xl border-2 border-blue-600 relative overflow-hidden flex flex-col transform hover:-translate-y-1 transition-all duration-300">
              <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl shadow-sm">
                ANBEFALT
              </div>
              <div className="p-8 flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-2">HMSVarsling – ekstern utredning</h3>
                <div className="text-4xl font-extrabold text-blue-600 mb-6 tracking-tight">
                  {formatPrice(prices.company.standard)} kr <span className="text-sm font-normal text-gray-500 align-middle">per sak</span>
                </div>
                <ul className="space-y-4">
                  {['Rask håndtering', 'Strukturert prosess', 'Uavhengig tredjepart', 'Standard valg'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="text-green-600 text-xs" />
                      </div>
                      <span className="font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 bg-blue-50/50 border-t border-blue-100">
                <button className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-blue-500/25">
                  Velg Standard
                </button>
              </div>
            </motion.div>

            {/* Card 2: Premium */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl shadow-lg border border-gray-200 flex flex-col transform hover:-translate-y-1 transition-all duration-300 hover:shadow-xl">
              <div className="p-8 flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Ekstern utreder</h3>
                <div className="text-4xl font-extrabold text-slate-900 mb-6 tracking-tight">
                  Fra {formatPrice(prices.company.externalFrom)} kr <span className="text-sm font-normal text-gray-500 align-middle">per sak</span>
                </div>
                <ul className="space-y-4">
                  {['Advokat, revisor eller spesialist', 'Spisskompetanse', 'Valgfritt tillegg'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <SafeIcon icon={FiCheck} className="text-slate-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-100">
                <button onClick={() => navigate('/utreder')} className="w-full bg-white border-2 border-gray-200 text-slate-700 py-4 rounded-xl font-bold hover:bg-gray-50 hover:border-gray-300 transition-all">
                  Se utredere
                </button>
              </div>
            </motion.div>
          </div>

          <p className="text-center text-slate-500 text-sm mt-8 italic max-w-2xl mx-auto bg-white px-4 py-2 rounded-full shadow-sm inline-block left-1/2 relative transform -translate-x-1/2 border border-gray-100">
            <SafeIcon icon={FiInfo} className="inline mr-2" />
            HMSVarsling er alltid standard og rimeligste alternativ. Ekstern utreder velges ved behov.
          </p>
        </section>

        <div className="border-t border-gray-200 w-full max-w-5xl mx-auto" />

        {/* SECTION 2: INVESTIGATOR PRICING */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 flex items-center justify-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg text-purple-600"><SafeIcon icon={FiUsers} /></div>
              For utredere
            </h2>
            <p className="text-slate-500 mt-3 text-lg">Medlemskap i vårt nettverk</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { name: 'Basic', price: prices.investigator.basic, items: ['Tilgang til saker', 'Utrederportal', 'Microsite'], btn: 'Velg Basic' },
              { name: 'Pro', price: prices.investigator.pro, items: ['Økt synlighet', 'Prioritet', 'Innsikt'], recommended: true, btn: 'Velg Pro' },
              { name: 'Partner', price: prices.investigator.partner, items: ['For utredere som selger HMSVarsling', 'Provisjon på salg', 'Foretrukket utreder-status'], btn: 'Bli Partner' }
            ].map((plan, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className={`bg-white rounded-2xl p-8 flex flex-col ${plan.recommended ? 'shadow-2xl border-2 border-purple-500 relative z-10 transform scale-105 md:-mt-4 md:mb-4' : 'shadow-lg border border-gray-100 hover:border-purple-200 transition-colors'}`}>
                {plan.recommended && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-purple-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md tracking-wide uppercase">
                    Mest Populær
                  </div>
                )}
                <h3 className="font-bold text-xl mb-2 text-slate-900">{plan.name}</h3>
                <div className="text-3xl font-extrabold mb-6 text-slate-900">{formatPrice(plan.price)} kr <span className="text-sm font-normal text-gray-500">/ mnd</span></div>
                <div className="flex-1">
                  <ul className="space-y-4 mb-8">
                    {plan.items.map((item, idx) => (
                      <li key={idx} className="text-sm text-slate-600 flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${plan.recommended ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                          <SafeIcon icon={FiCheck} className="text-xs" />
                        </div>
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button onClick={() => navigate('/utreder/register', { state: { plan: plan.name.toLowerCase() } })} className={`w-full py-3 rounded-xl text-sm font-bold transition-all shadow-sm ${plan.recommended ? 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-purple-500/25' : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'}`}>
                  {plan.btn}
                </button>
              </motion.div>
            ))}
          </div>
        </section>

        {/* SECTION 3: COMMISSION */}
        <section className="bg-gradient-to-r from-slate-50 to-white rounded-3xl p-8 md:p-12 max-w-4xl mx-auto border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-green-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50 pointer-events-none"></div>
          <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
            <div className="p-6 bg-white rounded-2xl text-green-600 shadow-md border border-green-100 flex-shrink-0">
              <SafeIcon icon={FiDollarSign} className="text-4xl" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Provisjon per sak</h3>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-2">Konsulent</div>
                  <div className="text-xl font-extrabold text-green-600">ca. {prices.commission.consultant} %</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-2">Advokat</div>
                  <div className="text-xl font-extrabold text-green-600">ca. {prices.commission.advocate} %</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-2">Partner</div>
                  <div className="text-xl font-extrabold text-green-600">{prices.commission.partnerMin}–{prices.commission.partnerMax} %</div>
                </div>
              </div>
              <p className="text-sm text-slate-500 flex items-center justify-center md:justify-start gap-2 bg-white/50 py-2 px-4 rounded-lg inline-block">
                <SafeIcon icon={FiInfo} /> Provisjon håndteres internt og er ikke synlig for bedrifter.
              </p>
            </div>
          </div>
        </section>
      </div>

      <ComplianceBar />
    </div>
  );
};

export default PricingInvestigationPage;